import React, { useEffect, useState } from "react";
import { aiApi, portfolioApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { TID } from "@/lib/testIds";
import { RefreshCw, Zap, Target, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function AISignalPanel({ symbol, onExecuted, settings }) {
  const [signal, setSignal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setSignal(await aiApi.signal(symbol));
    } catch (err) {
      console.error("[AISignalPanel] fetch failed", err);
      toast.error("Failed to fetch AI signal");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [symbol]);

  const autoExec = async () => {
    if (!signal) return;
    try {
      setExecuting(true);
      if (signal.action === "HOLD") { toast("Signal is HOLD — no trade"); return; }
      const budget = 100000 * ((settings?.position_size_pct || 5) / 100);
      const qty = Math.max(0.01, +(budget / signal.price).toFixed(4));
      const side = signal.action;
      await portfolioApi.trade({ symbol: signal.symbol, side, quantity: qty, price: signal.price });
      toast.success(`Executed ${side} ${qty} ${signal.symbol} @ $${signal.price}`);
      onExecuted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Execution failed");
    } finally {
      setExecuting(false);
    }
  };

  const action = signal?.action || "…";
  const color = action === "BUY" ? "text-emerald-400" : action === "SELL" ? "text-rose-400" : "text-amber-300";
  const bg = action === "BUY" ? "from-emerald-500/10" : action === "SELL" ? "from-rose-500/10" : "from-amber-500/10";

  return (
    <div className={`aq-card p-5 bg-gradient-to-br ${bg} to-transparent relative overflow-hidden`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="pulse-dot" />
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Claude Sonnet 5 · AI signal</div>
          </div>
          <div className="mt-3 flex items-end gap-4">
            <div className={`font-display text-5xl font-black ${color}`}>{action}</div>
            <div className="pb-1">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Confidence</div>
              <div className="font-mono font-bold text-xl">{signal?.confidence?.toFixed?.(1) ?? signal?.confidence ?? "--"}%</div>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button data-testid={TID.aiSignalRefresh} onClick={load} disabled={loading} variant="ghost"
            className="h-8 w-8 p-0 text-slate-400 hover:text-white hover:bg-white/10 rounded-full">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {signal && (
        <>
          <div className="mt-4 grid grid-cols-3 gap-3">
            <div className="rounded-lg bg-black/30 border border-white/5 p-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1"><Target className="w-3 h-3"/> Target</div>
              <div className="mt-1 font-mono font-bold text-emerald-300">${signal.target_price}</div>
            </div>
            <div className="rounded-lg bg-black/30 border border-white/5 p-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1"><ShieldAlert className="w-3 h-3"/> Stop</div>
              <div className="mt-1 font-mono font-bold text-rose-300">${signal.stop_loss}</div>
            </div>
            <div className="rounded-lg bg-black/30 border border-white/5 p-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Price</div>
              <div className="mt-1 font-mono font-bold">${signal.price}</div>
            </div>
          </div>
          <p className="mt-4 text-sm text-slate-300 leading-relaxed">{signal.rationale}</p>
          {signal.catalysts?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {signal.catalysts.map((c) => (
                <span key={c} className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full border border-white/10 bg-white/5 text-slate-300">{c}</span>
              ))}
            </div>
          )}
          <div className="mt-5 flex items-center justify-between">
            <div className="text-[11px] text-slate-500 font-mono">Generated {new Date(signal.generated_at).toLocaleTimeString()}</div>
            <Button
              data-testid={TID.aiAutoExecute}
              onClick={autoExec}
              disabled={executing || action === "HOLD"}
              className={`h-9 px-4 rounded-full text-xs font-semibold ${action === "BUY" ? "bg-emerald-500 hover:bg-emerald-400 text-black" : action === "SELL" ? "bg-rose-500 hover:bg-rose-400 text-white" : "bg-white/10 text-slate-400"}`}
            >
              <Zap className="w-3.5 h-3.5 mr-1.5" /> Auto-execute
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
